import { ThemeEvents, DiscountUpdateEvent } from "./events.js";

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.appliedDiscounts = new Set();
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.open = this.querySelector(".insert-code");
    this.error = this.querySelector(".discount-error");
    this.discountsList = this.querySelector(".applied-discounts-list");

    if (this.form) {
      this.form.addEventListener("submit", this.apply);
    }

    if (this.open) {
      this.open.addEventListener("click", this.toggle);
    }

    this.removeBtn = this.querySelector(".remove-discount");
    if (this.removeBtn) {
      this.removeBtn.addEventListener("click", this.clearAllDiscounts);
    }

    // Garantir que o campo input sempre venha limpo
    this.clearDiscountInput();

    // Carregar descontos salvos do localStorage
    this.loadSavedDiscounts();
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener("submit", this.apply);
    if (this.open) this.open.removeEventListener("click", this.toggle);
    this.teardownRefazer();
  }

  loadSavedDiscounts() {
    // Primeiro, carregar do localStorage
    const saved = localStorage.getItem("appliedDiscounts");
    if (saved) {
      try {
        const discounts = JSON.parse(saved);
        this.appliedDiscounts = new Set(discounts);
      } catch (e) {
        console.error("Erro ao carregar descontos salvos:", e);
      }
    }

    // Em seguida, sincronizar com o carrinho atual
    this.syncWithCartDiscounts();
    this.renderDiscountsList();
  }

  async syncWithCartDiscounts() {
    try {
      // Buscar dados atuais do carrinho
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const cartData = await response.json();

        // Verificar se há discount_codes aplicados
        if (cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
          const currentDiscounts = new Set();

          // Adicionar apenas códigos que estão realmente aplicados (applicable: true)
          cartData.discount_codes.forEach((discount) => {
            if (discount.applicable) {
              currentDiscounts.add(discount.code);
            }
          });

          // Atualizar nosso estado local
          this.appliedDiscounts = currentDiscounts;
          this.saveDiscounts();

          // log removido para produção
        }
      }
    } catch (error) {
      console.error("Erro ao sincronizar descontos com carrinho:", error);
    }
  }

  saveDiscounts() {
    localStorage.setItem(
      "appliedDiscounts",
      JSON.stringify([...this.appliedDiscounts])
    );
  }

  clearDiscountInput() {
    const discountInput = this.form?.querySelector('[name="discount"]');
    if (discountInput) {
      discountInput.value = "";
    }
  }

  toggle = (e) => {
    e.preventDefault();
    const isOpen = this.dataset.open === "opened";
    this.dataset.open = isOpen ? "closed" : "opened";
    this.dispatchEvent(
      new CustomEvent(ThemeEvents.DISCOUNT_TOGGLE, {
        detail: { open: !isOpen, sectionId: this.dataset.sectionId },
      })
    );

    // Sempre limpar o campo input, independente do estado
    this.clearDiscountInput();
    this.error.hidden = true;

    this.open.textContent = isOpen ? "Inserir código" : "Fechar";
  };

  apply = async (e) => {
    e.preventDefault();
    const discountCode = this.form
      .querySelector('[name="discount"]')
      .value.trim();

    if (!discountCode) {
      this.error.textContent = "Por favor, insira um código de desconto.";
      this.error.hidden = false;
      return;
    }

    if (this.appliedDiscounts.has(discountCode)) {
      this.error.textContent = "Este código de desconto já foi aplicado.";
      this.error.hidden = false;
      return;
    }

    // Adicionar estado de loading
    const submitButton =
      this.form.querySelector('button[type="submit"]') ||
      this.form.querySelector('input[type="submit"]');
    const originalButtonContent =
      submitButton?.innerHTML || submitButton?.value;

    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.innerHTML !== undefined) {
        submitButton.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" style="margin-right: 8px;">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
          Aplicando...
        `;
      } else {
        submitButton.value = "Aplicando...";
      }
    }

    try {
      // Para múltiplos descontos, enviar todos os códigos como string separada por vírgula
      const allDiscounts = [...this.appliedDiscounts, discountCode];
      const discountString = allDiscounts.join(",");

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: discountString,
        }),
      });

      const data = await response.json();

      if (data.status && data.status !== 200) {
        this.error.textContent = data.description;
        this.error.hidden = false;
        return;
      }

      // Adicionar o desconto à lista e salvar
      this.appliedDiscounts.add(discountCode);
      this.saveDiscounts();

      this.error.hidden = true;
      this.clearDiscountInput();

      // Sincronizar com dados do carrinho e renderizar
      await this.syncWithCartDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);
      this.error.textContent = "Erro ao aplicar desconto. Tente novamente.";
      this.error.hidden = false;
    } finally {
      // Restaurar botão
      if (submitButton) {
        submitButton.disabled = false;
        if (submitButton.innerHTML !== undefined) {
          submitButton.innerHTML = originalButtonContent || "Aplicar";
        } else {
          submitButton.value = originalButtonContent || "Aplicar";
        }
      }
    }
  };

  async renderDiscountsList() {
    if (!this.discountsList) return;

    if (this.appliedDiscounts.size === 0) {
      this.discountsList.innerHTML = "";
      this.discountsList.hidden = true;
      return;
    }

    // Buscar dados atuais do carrinho para verificar quais descontos são aplicáveis
    let cartData = null;
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (response.ok) {
        cartData = await response.json();
      }
    } catch (error) {
      console.error("Erro ao buscar dados do carrinho:", error);
    }

    const discountsArray = [...this.appliedDiscounts];

    // Layout horizontal similar ao exemplo mostrado
    this.discountsList.innerHTML = `
      <div class="applied-discounts-container">
        <div class="discount-tags">
          ${discountsArray
            .map((code) => {
              // Verificar se o desconto é aplicável
              let isApplicable = true;
              let discountInfo = null;

              if (cartData && cartData.discount_codes) {
                discountInfo = cartData.discount_codes.find(
                  (d) => d.code === code
                );
                isApplicable = discountInfo ? discountInfo.applicable : false;
              }

              return `
              <div class="discount-tag ${!isApplicable ? "discount-tag--not-applicable" : ""}">
                <span class="icon-discount-tag">
                  <svg width="16" height="16" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path stroke="#50AA6C" fill-rule="evenodd" clip-rule="evenodd" d="M0.6875 2.25C0.6875 1.11091 1.61091 0.1875 2.75 0.1875H13.25C14.3891 0.1875 15.3125 1.11091 15.3125 2.25V4.5C15.3125 4.81066 15.0607 5.0625 14.75 5.0625C14.5092 5.0625 14.2703 5.15392 14.0871 5.33709C13.721 5.7032 13.721 6.2968 14.0871 6.66291C14.2703 6.84608 14.5092 6.9375 14.75 6.9375C15.0607 6.9375 15.3125 7.18934 15.3125 7.5V9.75001C15.3125 10.8891 14.3891 11.8125 13.25 11.8125H2.75C1.61091 11.8125 0.6875 10.8891 0.6875 9.75V7.5C0.6875 7.18934 0.93934 6.9375 1.25 6.9375C1.4908 6.9375 1.72974 6.84608 1.91291 6.66291C2.27903 6.2968 2.27903 5.7032 1.91291 5.33709C1.72974 5.15392 1.4908 5.0625 1.25 5.0625C0.93934 5.0625 0.6875 4.81066 0.6875 4.5V2.25ZM2.75 1.3125C2.23223 1.3125 1.8125 1.73223 1.8125 2.25V4.01543C2.1405 4.10814 2.45037 4.28356 2.70841 4.54159C3.51386 5.34705 3.51386 6.65295 2.70841 7.45841C2.45037 7.71644 2.1405 7.89186 1.8125 7.98457V9.75C1.8125 10.2678 2.23223 10.6875 2.75 10.6875H5.9375V1.3125H2.75ZM7.0625 1.3125V10.6875H13.25C13.7678 10.6875 14.1875 10.2678 14.1875 9.75001V7.98457C13.8595 7.89186 13.5496 7.71644 13.2916 7.45841C12.4861 6.65295 12.4861 5.34705 13.2916 4.54159C13.5496 4.28356 13.8595 4.10814 14.1875 4.01543V2.25C14.1875 1.73223 13.7678 1.3125 13.25 1.3125H7.0625Z" fill="#292929"></path>
                  </svg>
                </span>
                <span class="discount-code">${code}</span>
                ${!isApplicable ? '<span class="discount-status">Não aplicável</span>' : ""}
                <button type="button" class="remove-single-discount" data-discount="${code}" aria-label="Remover ${code}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#7045F5"></path>
                  </svg>
                </button>
              </div>
            `;
            })
            .join("")}
        </div>
        <div class="discount-actions">
          <div class="discount-info">
            <span>Cupons aplicados (${discountsArray.length})</span>
          </div>
          <button type="button" class="remove-all-discounts">
            <span>Remover todos</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#00a0b1"></path>
            </svg>
          </button>
        </div>
      </div>
    `;

    this.discountsList.hidden = false;

    // Adicionar event listeners para os botões de remoção individual
    this.discountsList
      .querySelectorAll(".remove-single-discount")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Buscar o código do desconto tanto no botão quanto nos pais
          const button = e.target.closest(".remove-single-discount");
          const discountToRemove = button?.dataset?.discount;

          if (discountToRemove) {
            this.removeSingleDiscount(discountToRemove, button);
          }
        });
      });

    // Event listener para remover todos os cupons
    const removeAllBtn = this.discountsList.querySelector(
      ".remove-all-discounts"
    );
    if (removeAllBtn) {
      removeAllBtn.addEventListener("click", (e) => {
        this.clearAllDiscounts(e.target.closest(".remove-all-discounts"));
      });
    }
  }

  removeSingleDiscount = async (discountCode, buttonElement = null) => {
    try {
      // Adicionar estado de loading ao botão
      if (buttonElement) {
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }

      // Remover da lista local
      this.appliedDiscounts.delete(discountCode);

      // Estratégia: limpar todos os cupons e reaplicar os restantes
      const remainingDiscounts = [...this.appliedDiscounts];

      // Primeiro, limpar todos os cupons
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      // Se houver cupons restantes, reaplica-los um por um com delay
      if (remainingDiscounts.length > 0) {
        for (let i = 0; i < remainingDiscounts.length; i++) {
          const code = remainingDiscounts[i];
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ discount: code }),
          });

          // Pequeno delay entre as aplicações para garantir processamento
          if (i < remainingDiscounts.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }

      // Buscar dados atualizados do carrinho
      const finalResponse = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const data = await finalResponse.json();

      this.saveDiscounts();
      await this.syncWithCartDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Erro ao remover desconto:", error);

      // Restaurar botão em caso de erro
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#7045F5"></path>
          </svg>
        `;
      }
    }
  };

  clearAllDiscounts = async (buttonElement = null) => {
    try {
      // Adicionar estado de loading ao botão
      if (buttonElement) {
        const originalContent = buttonElement.innerHTML;
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
          <span>Removendo...</span>
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      const data = await response.json();

      this.appliedDiscounts.clear();
      this.saveDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);

      // Restaurar botão em caso de erro
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.innerHTML = `
          <span>Remover todos</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#00a0b1"></path>
          </svg>
        `;
      }
    }
  };

  teardownRefazer() {
    // Método de limpeza para a classe CartDiscount
    // Por enquanto vazio, pode ser expandido conforme necessário
  }
}

customElements.define("cart-discount-component", CartDiscount);
class CartShipping extends HTMLElement {
  constructor() {
    super();

    this.onToggleClick = this.toggle.bind(this);
    this.onApplyClick = this.apply.bind(this);
    this.onFormSubmit = this.handleSubmit.bind(this);
    this.onCepKeydown = this.handleCepKeydown.bind(this);
    this.onPopupOverlayClick = this.handlePopupOverlayClick.bind(this);
    this.onPopupCloseClick = this.handlePopupCloseClick.bind(this);
    this.onClearShippingClick = this.clearShipping.bind(this);
    this._calcBtn = null;
    this._cepInput = null;
    this._popup = null;
    this._popupCloseBtn = null;
    this._refazerBtn = null;
  }

  fmtBRL(v) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(v) || 0);
  }

  normalizeDate(dateLike) {
    const d = new Date(dateLike);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  addBusinessDays(startDate, businessDays, holidays = []) {
    const date = this.normalizeDate(startDate);
    let added = 0;
    const totalDays = Number(businessDays) || 0;

    while (added < totalDays) {
      date.setDate(date.getDate() + 1);

      const day = date.getDay();
      const isWeekend = day === 0 || day === 6;
      const isHoliday = this.isHoliday(date, holidays);

      if (!isWeekend && !isHoliday) {
        added++;
      }
    }

    return date;
  }

  formatDeadlineText(days, isBusiness = true) {
    const numericDays = Number(days) || 0;
    if (numericDays === 1) {
      return isBusiness ? "Em até 1 dia útil" : "Em até 1 dia";
    }
    return isBusiness
      ? `Em até ${numericDays} dias úteis`
      : `Em até ${numericDays} dias`;
  }

  deliveryHtml(range) {
    if (!range) return "";

    if (typeof range === "string") {
      const matchBusiness = range.match(/(\d+)\s*dias?\s*úteis/i);
      if (matchBusiness) {
        return this.formatDeadlineText(parseInt(matchBusiness[1], 10), true);
      }

      const matchDays = range.match(/(\d+)\s*dias?/i);
      if (matchDays) {
        return this.formatDeadlineText(parseInt(matchDays[1], 10), false);
      }

      return range;
    }

    const min = range.min ?? range.minimum ?? range.from ?? range[0] ?? null;
    const max = range.max ?? range.maximum ?? range.to ?? range[1] ?? null;
    const unit = String(
      range.unit || range.units || range.type || ""
    ).toLowerCase();

    if (typeof min === "number" || typeof max === "number") {
      const same = (min ?? max) === (max ?? min);
      const value = same ? (min ?? max) : (max ?? min);
      const isBusiness = unit.includes("business") || unit.includes("útil");

      return this.formatDeadlineText(value, isBusiness);
    }

    // Ignorar datas e mostrar apenas prazo em dias
    return "";
  }

  connectedCallback() {
    this.form =
      this.querySelector(".shipping-component-form") ||
      this.querySelector(".shipping-form") ||
      this.querySelector(".frete-form") ||
      this.querySelector("form") ||
      this.querySelector("div[role='form']");
    this.open = this.querySelector(".insert-cep");
    this.error = this.querySelector(".shipping-component-error");
    this.ensurePopup();
    if (this.form) {
      this.form.addEventListener("submit", this.onFormSubmit, true);
      this._cepInput = this.form.querySelector('[name="shipping-component"]');
      this._cepInput?.addEventListener("keydown", this.onCepKeydown, true);
      this._calcBtn =
        this.form.querySelector('button[type="submit"]') ||
        this.form.querySelector('input[type="submit"]');
    }
    this.open?.addEventListener("click", this.onToggleClick);
    /* const selectedRate = localStorage.getItem("selectedShippingRate"); */
    /* if (selectedRate) {
      try {
        this.renderSelected(JSON.parse(selectedRate));
      } catch (e) {
        localStorage.removeItem("selectedShippingRate");
      }
    } else { */
    const result = this.querySelector(".selected-shipping-components");
    if (result) {
      const wrapper = result.querySelector(".shipping-wrapper");
      if (wrapper) wrapper.innerHTML = "";
      result.classList.add("hidden");
    }
    /* } */
  }

  disconnectedCallback() {
    this.open?.removeEventListener("click", this.onToggleClick);
    this.form?.removeEventListener("submit", this.onFormSubmit, true);
    this._cepInput?.removeEventListener("keydown", this.onCepKeydown, true);
    this.teardownPopup();
    this.teardownRefazer();
  }

  ensurePopup() {
    let popup = document.getElementById("shipping-popup");
    if (!popup) {
      popup = document.createElement("div");
      popup.id = "shipping-popup";
      popup.style.position = "fixed";
      popup.style.inset = "0";
      popup.style.width = "100vw";
      popup.style.height = "100vh";
      popup.style.background = "rgba(0,0,0,0.5)";
      popup.style.display = "none";
      popup.style.justifyContent = "center";
      popup.style.alignItems = "center";
      popup.style.zIndex = "9999";
      popup.innerHTML = `
        <div
          id="shipping-popup-content"
          style="
            background:#fff;
            max-width:420px;
            width:90vw;
            max-height:80vh;
            overflow:auto;
            padding:24px 16px 16px;
            border-radius:8px;
            position:relative;
            box-shadow:0 2px 16px rgba(0,0,0,0.2);
          "
        >
          <button
            id="close-shipping-popup"
            type="button"
            style="
              position:absolute;
              top:8px;
              right:8px;
              background:none;
              border:none;
              font-size:1.5rem;
              cursor:pointer;
            "
          >&times;</button>

          <h3 style="margin-top:0;">Opções de Frete</h3>
          <div id="shipping-popup-list"></div>
        </div>
      `;
      document.body.appendChild(popup);
    }
    this._popup = popup;
    this._popupCloseBtn = document.getElementById("close-shipping-popup");
    this._popup.removeEventListener("click", this.onPopupOverlayClick);
    this._popup.addEventListener("click", this.onPopupOverlayClick);
    this._popupCloseBtn?.removeEventListener("click", this.onPopupCloseClick);
    this._popupCloseBtn?.addEventListener("click", this.onPopupCloseClick);
  }

  teardownPopup() {
    this._popup?.removeEventListener("click", this.onPopupOverlayClick);
    this._popupCloseBtn?.removeEventListener("click", this.onPopupCloseClick);
  }

  handlePopupOverlayClick(e) {
    if (e.target === this._popup) {
      this.hidePopup();
    }
  }

  handlePopupCloseClick() {
    this.hidePopup();
  }

  showPopup() {
    if (this._popup) {
      this._popup.style.display = "flex";
      document.body.style.overflow = "hidden";
    }
  }

  hidePopup() {
    if (this._popup) {
      this._popup.style.display = "none";
      document.body.style.overflow = "";
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    this.apply();
    return false;
  }

  handleCepKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      this.apply();
      return false;
    }
  }

  setError(message = "") {
    if (!this.error) return;
    if (message) {
      this.error.textContent = message;
      this.error.hidden = false;
    } else {
      this.error.textContent = "";
      this.error.hidden = true;
    }
  }

  toggle(e) {
    e.preventDefault();
    const isOpen = this.dataset.open === "opened";
    this.dataset.open = isOpen ? "closed" : "opened";
    if (isOpen) {
      if (this._cepInput) this._cepInput.value = "";
      this.setError("");
    }
    if (this.open) {
      this.open.textContent = isOpen ? "Calcule seu frete" : "Fechar";
    }
  }

  getCookie(name) {
    var value = "; " + document.cookie;
    var parts = value.split("; " + name + "=");
    if (parts.length == 2) return parts.pop().split(";").shift();
  }

  updateCartCookie(a) {
    var date = new Date();
    date.setTime(date.getTime() + 14 * 86400000);
    var expires = "; expires=" + date.toGMTString();
    document.cookie = "cart=" + a + expires + "; path=/";
  }

  async apply(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    }
    const raw = this._cepInput?.value || "";
    const zip = raw.replace(/\D/g, "");
    if (zip.length !== 8) {
      this.setError("Informe um CEP válido (8 dígitos).");
      this.renderPopupList([]);
      this.showPopup();
      return;
    }

    let province = null;
    try {
      const viaCepRes = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
      const viaCepData = await viaCepRes.json();
      if (viaCepData && viaCepData.uf) {
        province = viaCepData.uf;
      }
    } catch (err) {
      province = null;
    }
    if (!province) {
      this.setError(
        "Não foi possível identificar o estado (UF) pelo CEP. Informe um CEP válido."
      );
      this.renderPopupList([]);
      this.showPopup();
      return;
    }
    const spinner = this.form?.querySelector(".form-loading");
    spinner?.classList.remove("hidden");
    this.setError("");
    if (this._calcBtn) {
      this._calcBtn.disabled = true;
    }
    try {
      const root = window.Shopify?.routes?.root || "/";

      let currCartCookie, tempCookieValue, fakeCookieValue;
      if (this.classList.contains("cart-shipping-pdp")) {
        currCartCookie = this.getCookie("cart");
        tempCookieValue = "temp-cart-cookie___" + Date.now();
        fakeCookieValue = "fake-cart-cookie___" + Date.now();
        if (!currCartCookie) {
          this.updateCartCookie(tempCookieValue);
          currCartCookie = this.getCookie("cart");
        }

        this.updateCartCookie(fakeCookieValue);

        let formData = { items: [] };
        if (document.body.classList.contains("template-suffix-kit")) {
          formData.items = [];
          const ids = Array.from(
            document.querySelectorAll("product-kit-item")
          ).map((item) => item.getAttribute("variant-id"));

          ids.forEach((id) => formData.items.push({ id, quantity: 1 }));
        } else {
          const qtInput = document.querySelector(
            "product-form .quantity__input"
          );
          const idInput = document.querySelector(
            'product-form input[name="id"]'
          );

          formData = {
            items: [
              {
                id: idInput.value,
                quantity: qtInput.value,
              },
            ],
          };
        }

        await fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        })
          .then((response) => {
            return response.json();
          })
          .catch((error) => {
            console.error("Error:", error);
            this.updateCartCookie(currCartCookie);
          });
      }

      const prepareRes = await fetch(
        `${root}cart/prepare_shipping_rates.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            shipping_address: {
              zip,
              country: "Brazil",
              province,
            },
          }),
        }
      );

      if (!prepareRes.ok && prepareRes.status !== 202) {
        let errData = null;
        let errText = null;

        try {
          errData = await prepareRes.json();
        } catch (_) {
          try {
            errText = await prepareRes.text();
          } catch (__) {
            errText = null;
          }
        }

        throw new Error(
          errData?.error ||
            errData?.errors ||
            errText ||
            `Não foi possível iniciar o cálculo de frete. [${prepareRes.status}]`
        );
      }

      const params = new URLSearchParams({
        "shipping_address[zip]": zip,
        "shipping_address[country]": "Brazil",
      });

      const rates = await this.pollRates(
        `${root}cart/async_shipping_rates.json?${params.toString()}`
      );

      if (!Array.isArray(rates) || !rates.length) {
        this.setError("Nenhuma opção de frete para este CEP.");
        this.renderPopupList([]);
        this.showPopup();
        if (this.classList.contains("cart-shipping-pdp")) {
          this.updateCartCookie(currCartCookie);
        }
        return;
      }

      rates.sort((a, b) => Number(a.price) - Number(b.price));

      if (this.classList.contains("cart-shipping-pdp")) {
        this.updateCartCookie(currCartCookie);
        this.renderList(rates);
      } else {
        this.renderPopupList(rates);
        this.showPopup();
      }
    } catch (err) {
      this.setError(
        err?.message || "Erro ao consultar frete. Tente novamente."
      );
      this.renderPopupList([]);
      this.showPopup();
    } finally {
      spinner?.classList.add("hidden");

      if (this._calcBtn) {
        this._calcBtn.disabled = false;
      }
    }
  }

  async pollRates(url, { tries = 20, delay = 700 } = {}) {
    for (let i = 0; i < tries; i++) {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      let data = null;

      try {
        data = await res.json();
      } catch (_) {
        data = null;
      }

      if (Array.isArray(data?.shipping_rates)) {
        return data.shipping_rates;
      }

      if (Array.isArray(data)) {
        return data;
      }

      if (data?.error) {
        throw new Error(
          Array.isArray(data.error) ? data.error.join(", ") : String(data.error)
        );
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    return [];
  }

  renderList(rates) {
    const result = this.querySelector(".selected-shipping-components");
    if (result) {
      result.classList.remove("hidden");
      result.style.display = "";
      const wrapper = result.querySelector(".shipping-wrapper");
      if (wrapper) wrapper.innerHTML = "";

      wrapper.innerHTML = `
        ${rates
          .map((r, i) => {
            return `
              <li>
                <span class="shipping-name">${r.name}</span>
                <span class="shipping-price">${Number(r.price) === 0 ? "Grátis" : this.fmtBRL(r.price)}</span>
                <span class="shipping-date">chega em ${r.delivery_days[0] == 1 ? `1 dia útil` : `${r.delivery_days[0]} dias úteis`}</span>
              </li>
            `;
          })
          .join("")}
      `;
    }
  }

  renderPopupList(rates) {
    const popupList = document.getElementById("shipping-popup-list");
    if (!popupList) return;

    if (!Array.isArray(rates) || !rates.length) {
      popupList.innerHTML = `
        <div style="color:#e00;text-align:center;padding:16px;">
          Nenhuma opção de frete disponível para este CEP.
        </div>
      `;
      return;
    }

    let selectedIdx = 0;

    popupList.innerHTML = `
      <ul style="list-style:none;padding:0;margin:0;">
        ${rates
          .map((r, i) => {
            return `
              <li>
                <label>
                  <input type="radio" name="shipping-option" value="${i}">
                  <span class="shipping-name">${r.name}</span>
                  <span class="shipping-price">${Number(r.price) === 0 ? "Grátis" : this.fmtBRL(r.price)}</span>
                  <span class="shipping-date">chega em ${r.delivery_days[0] == 1 ? `1 dia útil` : `${r.delivery_days[0]} dias úteis`}</span>
                </label>
              </li>
            `;
          })
          .join("")}
      </ul>

      <button
        id="select-shipping-popup-btn"
        type="button"
        style="
          width:100%;
          background:#232323;
          color:#fff;
          padding:10px 0;
          border:none;
          border-radius:4px;
          font-size:1.1em;
          cursor:pointer;
        "
      >
        Selecionar Frete
      </button>
    `;

    const radios = popupList.querySelectorAll('input[name="shipping-option"]');
    radios.forEach((input) => {
      input.addEventListener("change", (ev) => {
        selectedIdx = Number(ev.target.value);
      });
    });

    const selectBtn = popupList.querySelector("#select-shipping-popup-btn");
    selectBtn?.addEventListener("click", () => {
      const selected = rates[selectedIdx];

      if (!selected) {
        selectBtn.textContent = "Selecione uma opção";
        setTimeout(() => {
          selectBtn.textContent = "Selecionar Frete";
        }, 1200);
        return;
      }

      /* localStorage.setItem("selectedShippingRate", JSON.stringify(selected)); */
      this.hidePopup();
      this.renderSelectedOnAllComponents(selected);

      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    });
  }

  renderSelectedOnAllComponents(selected) {
    const allComponents = document.querySelectorAll("cart-shipping-component");

    allComponents.forEach((component) => {
      if (typeof component.renderSelected === "function") {
        component.renderSelected(selected);
      }
    });
  }

  renderSelected(selected) {
    const listBox = this.querySelector(".selected-shipping-components");
    if (!listBox) return;

    const wrapper = listBox.querySelector(".shipping-wrapper");
    let resume = listBox.querySelector(".shipping-resume");

    if (!resume) {
      resume = document.createElement("div");
      resume.className = "shipping-resume list-unstyled";
      listBox.appendChild(resume);
    }

    if (wrapper) wrapper.style.display = "none";

    listBox.classList.remove("hidden");
    this.classList.add("shipping-component-applied");

    const prazo = selected.delivery_range
      ? this.deliveryHtml(selected.delivery_range)
      : selected.delivery_days && selected.delivery_days.length
        ? this.formatDeadlineText(selected.delivery_days[0], true)
        : "";

    resume.innerHTML = `
      <li class="shipping-component-item">
        <span class="shipping-selected-wrapper">
          <span>${selected.name || "Frete"} ${prazo ? `(${prazo})` : ""}</span>
        </span>
        <span>${Number(selected.price) === 0 ? "Grátis" : this.fmtBRL(selected.price)}</span>
        <button type="button" class="remove-shipping-component" aria-label="Refazer cálculo">&#10005;</button>
      </li>
    `;

    this.setAttribute("data-open", "closed");

    if (this.open) {
      this.open.textContent = "Calcule seu frete";
    }

    this.teardownRefazer();
    this._refazerBtn = resume.querySelector(".remove-shipping-component");
    this._refazerBtn?.addEventListener("click", this.onClearShippingClick);
  }

  teardownRefazer() {
    if (this._refazerBtn) {
      this._refazerBtn.removeEventListener("click", this.onClearShippingClick);
      this._refazerBtn = null;
    }
  }

  clearShipping() {
    /* localStorage.removeItem("shippingRates"); */
    /* localStorage.removeItem("selectedShippingRate"); */

    const allComponents = document.querySelectorAll("cart-shipping-component");

    allComponents.forEach((component) => {
      const listBox = component.querySelector(".selected-shipping-components");
      const wrapper = listBox?.querySelector(".shipping-wrapper");
      const resume = listBox?.querySelector(".shipping-resume");

      if (resume) resume.remove();
      if (wrapper) wrapper.style.display = "";

      listBox?.classList.add("hidden");
      component.classList.remove("shipping-component-applied");
      component.dataset.open = "opened";

      if (component.open) {
        component.open.textContent = "Fechar";
      }

      if (component._cepInput) {
        component._cepInput.value = "";
      }

      if (typeof component.setError === "function") {
        component.setError("");
      }
    });

    if (this._cepInput) {
      this._cepInput.focus();
    }

    if (window.location.pathname === "/cart") {
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  }
}

customElements.define("cart-shipping-component", CartShipping);
